import CircleIcon from "@mui/icons-material/Circle";
// import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  ButtonBase,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  useGetTenantKpiDisplay,
  useGetTenantKpiList,
} from "services/v1/Tenant/KpiService";
import {
  TenantKpiConsolidationMeasurement,
  TenantKpiConsolidationSummary,
  TenantKpiDisplayItem,
} from "types/api/Tenant/KpiTypes";

type KpiTableItem = {
  id: string;
  name: string;
  period: string;
  detail: string;
  value: string;
  targetStatus: string;
  statusLabel: string;
};

type KpiTableMeasurement = {
  id: string;
  label: string;
  value: string;
  percentage: string;
  targetStatus: string;
  statusLabel: string;
  trendStatus: string;
  trendLabel: string;
};

type KpiTableRowItem = KpiTableItem & {
  measurements: KpiTableMeasurement[];
  trendStatus: string;
  trendLabel: string;
};

type KpiSummaryItem = {
  id: string;
  period: string;
  value: string;
  badge: string;
  targetStatus: string;
  trendStatus: string;
  trendLabel: string;
  trendAriaLabel: string;
};

type KpiSummaryGroup = {
  id: string;
  title: string;
  items: KpiSummaryItem[];
};

type KPIDisplayProps = {
  onVehicleDetails?: (item: KpiTableItem) => void;
  selectedKpiId?: string | null;
  onKpiSelect?: (kpiDefinitionId: string) => void;
};

function mapKpiDisplayItem(item: TenantKpiDisplayItem): KpiTableRowItem {
  const orderedDisplayValues = [...item.displayValues].sort(
    (a, b) => a.order - b.order
  );
  const detail = orderedDisplayValues
    .map((value) => value.formattedValue)
    .filter(Boolean)
    .join(" · ");
  const measurements = orderedDisplayValues.map((value) => ({
    id: `${value.attributeId}:${value.order}`,
    label: value.displayName,
    value: value.formattedValue || "—",
    percentage:
      value.currentVariancePercentageFormattedValue ||
      item.currentVariancePercentageFormattedValue ||
      "—",
    targetStatus: value.targetStatus || item.targetStatus,
    statusLabel: value.targetStatusDisplayName || item.targetStatusDisplayName,
    trendStatus: value.trendStatus || item.trendStatus,
    trendLabel: value.trendStatusDisplayName || item.trendStatusDisplayName,
  }));

  return {
    id: String(item.member.value),
    name: item.member.label,
    period: item.selectedColumn.displayName,
    detail,
    value: item.currentVariancePercentageFormattedValue || "—",
    targetStatus: item.targetStatus,
    statusLabel: item.targetStatusDisplayName,
    trendStatus: item.trendStatus,
    trendLabel: item.trendStatusDisplayName,
    measurements,
  };
}

function getMeasurementBadgeLabel(measurement: KpiTableMeasurement) {
  const percentage = measurement.percentage.replace(/^-/, "");
  const statusLabel = measurement.statusLabel.trim().toLocaleLowerCase();

  if (percentage === "—") {
    return statusLabel || percentage;
  }

  return statusLabel ? `${percentage} ${statusLabel}` : percentage;
}

function getMeasurementLabel(label: string) {
  return label.replace(/\s+per\s+/gi, " / ").toLocaleUpperCase();
}

function getTargetStatusColors(targetStatus: string) {
  if (targetStatus.includes("BELOW")) {
    return { color: "#D92D20", backgroundColor: "#FEE4E2" };
  }

  if (targetStatus.includes("ABOVE")) {
    return { color: "#078A5B", backgroundColor: "#D1FADF" };
  }

  return { color: "#475467", backgroundColor: "#EAECF0" };
}

function getTrendStatusColors(trendStatus: string) {
  if (trendStatus.includes("DECLINING")) {
    return { color: "#D92D20", backgroundColor: "#FEE4E2" };
  }

  if (trendStatus.includes("IMPROVING")) {
    return { color: "#078A5B", backgroundColor: "#D1FADF" };
  }

  return { color: "#475467", backgroundColor: "#EAECF0" };
}

function TrendIndicator({
  trendStatus,
  trendLabel,
  ariaLabel,
}: {
  trendStatus: string;
  trendLabel: string;
  ariaLabel: string;
}) {
  if (!trendStatus && !trendLabel.trim()) {
    return null;
  }

  const colors = getTrendStatusColors(trendStatus);
  const TrendIcon = trendStatus.includes("DECLINING")
    ? TrendingDownIcon
    : trendStatus.includes("IMPROVING")
    ? TrendingUpIcon
    : CircleIcon;
  const displayLabel =
    trendLabel.trim() ||
    (trendStatus.includes("DECLINING")
      ? "Declining"
      : trendStatus.includes("IMPROVING")
      ? "Improving"
      : "Stable");

  return (
    <Stack
      component="span"
      direction="row"
      alignItems="center"
      aria-label={ariaLabel}
      sx={{ mt: 0.6, color: colors.color }}
    >
      <TrendIcon aria-hidden="true" sx={{ mr: 0.45, fontSize: 15 }} />
      <Typography
        component="span"
        sx={{ color: "inherit", fontSize: 11, fontWeight: 800, lineHeight: 1 }}
      >
        {displayLabel}
      </Typography>
    </Stack>
  );
}

function getKpiBannerColors(targetStatus: string, trendStatus: string) {
  if (targetStatus.includes("ABOVE") && trendStatus.includes("IMPROVING")) {
    return {
      color: "#067647",
      backgroundColor: "#D1FADF",
      borderColor: "#ABEFC6",
      bannerBackgroundColor: "#ECFDF3",
    };
  }

  if (targetStatus.includes("ABOVE") && trendStatus.includes("DECLINING")) {
    return {
      color: "#B54708",
      backgroundColor: "#FEF0C7",
      borderColor: "#FEDF89",
      bannerBackgroundColor: "#FFFAEB",
    };
  }

  if (targetStatus.includes("BELOW") && trendStatus.includes("IMPROVING")) {
    return {
      color: "#175CD3",
      backgroundColor: "#D1E9FF",
      borderColor: "#B2DDFF",
      bannerBackgroundColor: "#EFF8FF",
    };
  }

  if (targetStatus.includes("BELOW") && trendStatus.includes("DECLINING")) {
    return {
      color: "#B42318",
      backgroundColor: "#FEE4E2",
      borderColor: "#FECDCA",
      bannerBackgroundColor: "#FEF3F2",
    };
  }

  return {
    color: "#475467",
    backgroundColor: "#EAECF0",
    borderColor: "#E0E5ED",
    bannerBackgroundColor: "#F8F9FB",
  };
}

function getConsolidationMeasurements(
  summary: TenantKpiConsolidationSummary
): TenantKpiConsolidationMeasurement[] {
  if (summary.measurements?.length) {
    return [...summary.measurements].sort(
      (a, b) => a.measurement.order - b.measurement.order
    );
  }

  if (summary.columns?.length) {
    return [
      {
        measurement: summary.columns[0].displayValue,
        columns: summary.columns,
      },
    ];
  }

  return [];
}

const focusRing = {
  "&:focus-visible": {
    outline: "3px solid rgba(63, 80, 181, 0.22)",
    outlineOffset: 2,
  },
};

function VehiclePanel({
  title,
  vehicles,
  tone,
  onVehicleDetails,
}: {
  title: string;
  vehicles: KpiTableRowItem[];
  tone: "attention" | "performing";
  onVehicleDetails?: (item: KpiTableItem) => void;
}) {
  const isAttention = tone === "attention";
  const accent = isAttention ? "#D92D20" : "#0E9F6E";
  const headerBackground = isAttention ? "#FFF4F3" : "#F0FAF7";

  return (
    <Box
      sx={{
        overflow: "hidden",
        border: "1px solid #E2E7EF",
        borderRadius: "7px",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ minHeight: 44, px: 1.75, backgroundColor: headerBackground }}
      >
        {/* <CircleIcon
          aria-hidden="true"
          sx={{ mr: 1, color: accent, fontSize: 12 }}
        /> */}
        <Typography
          sx={{
            color: isAttention ? "#A72A24" : "#127455",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{ ml: "auto", color: accent, fontSize: 12, fontWeight: 800 }}
        >
          {vehicles.length} items
        </Typography>
      </Stack>

      <Box sx={{ px: 1.75, overflowX: "auto" }}>
        {vehicles.length === 0 ? (
          <Typography
            sx={{ py: 3, color: "#667085", fontSize: 12, textAlign: "center" }}
          >
            No items
          </Typography>
        ) : (
          vehicles.map((vehicle, index) => {
            const targetColors = getTargetStatusColors(vehicle.targetStatus);
            const hasMultipleMeasurements = vehicle.measurements.length > 1;

            return (
              <Box
                key={`${vehicle.id}:${vehicle.period}:${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: hasMultipleMeasurements
                    ? {
                        xs: "minmax(0, 1fr) auto",
                        sm: "minmax(155px, 1.1fr) minmax(250px, 2fr) auto",
                      }
                    : {
                        xs: "minmax(0, 1fr) auto",
                        sm: "minmax(0, 1fr) auto auto auto",
                      },
                  alignItems: "center",
                  columnGap: { xs: 1, sm: 1.8 },
                  rowGap: 1,
                  minHeight: hasMultipleMeasurements
                    ? { xs: 112, sm: 92 }
                    : { xs: 82, sm: 66 },
                  py: 1.1,
                  borderTop: index === 0 ? "none" : "1px solid #E8ECF2",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      color: "#344054",
                      fontSize: 15,
                      fontWeight: 800,
                      lineHeight: 1.25,
                    }}
                  >
                    {vehicle.name}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.35,
                      color: "#7B8495",
                      fontSize: 12,
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {vehicle.period}
                    {!hasMultipleMeasurements && vehicle.detail
                      ? ` · ${vehicle.detail}`
                      : ""}
                  </Typography>
                </Box>

                {hasMultipleMeasurements ? (
                  <Box
                    sx={{
                      gridColumn: { xs: "1 / -1", sm: "auto" },
                      display: "grid",
                      gridTemplateColumns: `repeat(${vehicle.measurements.length}, minmax(120px, 1fr))`,
                      gap: { xs: 1.2, sm: 1.8 },
                      minWidth: vehicle.measurements.length * 120,
                    }}
                  >
                    {vehicle.measurements.map((measurement) => {
                      const measurementColors = getTargetStatusColors(
                        measurement.targetStatus
                      );

                      return (
                        <Box key={measurement.id} sx={{ minWidth: 0 }}>
                          <Typography
                            noWrap
                            sx={{
                              color: "#7B8495",
                              fontSize: 11,
                              fontWeight: 800,
                              lineHeight: 1.15,
                              textTransform: "uppercase",
                            }}
                          >
                            {getMeasurementLabel(measurement.label)}
                          </Typography>
                          <Typography
                            noWrap
                            sx={{
                              mt: 0.25,
                              color: "#344054",
                              fontSize: 16,
                              fontWeight: 800,
                              lineHeight: 1.1,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {measurement.value}
                          </Typography>
                          <Box
                            sx={{
                              display: "inline-flex",
                              mt: 0.7,
                              minWidth: 86,
                              maxWidth: "100%",
                              px: 1.4,
                              py: 0.65,
                              borderRadius: 99,
                              color: measurementColors.color,
                              backgroundColor:
                                measurementColors.backgroundColor,
                              fontSize: 11,
                              fontWeight: 800,
                              lineHeight: 1,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getMeasurementBadgeLabel(measurement)}
                          </Box>
                          <TrendIndicator
                            trendStatus={measurement.trendStatus}
                            trendLabel={measurement.trendLabel}
                            ariaLabel={`${measurement.label} trend: ${measurement.trendLabel}`}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <>
                    <Typography
                      sx={{
                        color: "#344054",
                        fontSize: 16,
                        fontWeight: 800,
                        lineHeight: 1,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {vehicle.value}
                    </Typography>

                    <Stack
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      sx={{
                        justifySelf: { xs: "start", sm: "center" },
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 86,
                          px: 1.6,
                          py: 0.65,
                          borderRadius: 99,
                          color: targetColors.color,
                          backgroundColor: targetColors.backgroundColor,
                          fontSize: 12,
                          fontWeight: 800,
                          lineHeight: 1,
                          textAlign: "center",
                        }}
                      >
                        {vehicle.statusLabel}
                      </Box>
                      <TrendIndicator
                        trendStatus={vehicle.trendStatus}
                        trendLabel={vehicle.trendLabel}
                        ariaLabel={`${vehicle.name} trend: ${vehicle.trendLabel}`}
                      />
                    </Stack>
                  </>
                )}
                {/*
                <ButtonBase
                  type="button"
                  onClick={() =>
                    onVehicleDetails?.({
                      id: vehicle.id,
                      name: vehicle.name,
                      period: vehicle.period,
                      detail: vehicle.detail,
                      value: vehicle.value,
                      targetStatus: vehicle.targetStatus,
                      statusLabel: vehicle.statusLabel,
                    })
                  }
                  aria-label={`View details for ${vehicle.name}`}
                  sx={{
                    ...focusRing,
                    gridColumn: hasMultipleMeasurements
                      ? { xs: 2, sm: "auto" }
                      : "auto",
                    gridRow: hasMultipleMeasurements
                      ? { xs: 1, sm: "auto" }
                      : "auto",
                    justifySelf: "end",
                    borderRadius: 1,
                    color: "#0675F7",
                    fontSize: 12,
                    fontWeight: 800,
                    "&:hover": {
                      color: "#005FCC",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Details
                  <ChevronRightIcon
                    aria-hidden="true"
                    sx={{ ml: 0.1, fontSize: 12 }}
                  />
                </ButtonBase> */}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}

export function KPIDisplay({
  onVehicleDetails,
  selectedKpiId: requestedKpiId,
  onKpiSelect,
}: KPIDisplayProps) {
  const { data: kpiListResponse, isLoading, isError } = useGetTenantKpiList();
  const [localSelectedKpiId, setLocalSelectedKpiId] = useState<string | null>(
    null
  );
  const [selectedTimePeriods, setSelectedTimePeriods] = useState<
    Record<string, string>
  >({});
  const kpiTabs = kpiListResponse?.data ?? [];
  const selectedKpiId = requestedKpiId ?? localSelectedKpiId;
  const selectedKpi =
    kpiTabs.find((kpi) => kpi.id === selectedKpiId) ?? kpiTabs[0];
  const selectedTimePeriod = selectedKpi
    ? selectedTimePeriods[selectedKpi.id]
    : undefined;
  const {
    data: kpiDisplayResponse,
    isLoading: isDisplayLoading,
    isError: isDisplayError,
  } = useGetTenantKpiDisplay(selectedKpi?.id, selectedTimePeriod);
  const kpiDisplay = kpiDisplayResponse?.data;
  const displayedTimePeriod =
    selectedTimePeriod || kpiDisplay?.currentTimePeriod || "";
  const timePeriodOptions = kpiDisplay?.timePeriodOptions ?? [];
  const hasDisplayedTimePeriodOption = timePeriodOptions.some(
    (option) => String(option.value) === displayedTimePeriod
  );
  const selectedKpiName =
    kpiDisplay?.name.trim() || selectedKpi?.name.trim() || "KPI";
  const attentionItems =
    kpiDisplay?.worst.map((item) => mapKpiDisplayItem(item)) ?? [];
  const performingItems =
    kpiDisplay?.best.map((item) => mapKpiDisplayItem(item)) ?? [];
  const consolidationGroups: KpiSummaryGroup[] =
    kpiDisplay?.consolidationSummary?.flatMap((summary) =>
      getConsolidationMeasurements(summary).map((measurement) => ({
        id: `${summary.member.value}:${measurement.measurement.attributeId}`,
        title: measurement.measurement.displayName
          ? `Overall ${measurement.measurement.displayName.toLocaleLowerCase()}`
          : "Overall",
        items: [...measurement.columns]
          .sort((a, b) => a.column.order - b.column.order)
          .map((column) => ({
            id: `${summary.member.value}:${measurement.measurement.attributeId}:${column.column.columnId}`,
            period: column.column.displayName,
            value: column.displayValue.formattedValue || "—",
            badge:
              column.badgeDisplayValue || column.targetStatusDisplayName || "—",
            targetStatus: column.targetStatus,
            trendStatus: measurement.trendStatus || "",
            trendLabel: measurement.trendStatusDisplayName || "",
            trendAriaLabel: `${measurement.measurement.displayName} ${column.column.displayName} trend: ${measurement.trendStatusDisplayName}`,
          })),
      }))
    ) ?? [];
  const consolidationMemberName =
    kpiDisplay?.consolidationSummary?.[0]?.member.label.trim() || "";
  const firstConsolidationMeasurements = kpiDisplay?.consolidationSummary?.[0]
    ? getConsolidationMeasurements(kpiDisplay.consolidationSummary[0])
    : [];
  const headlineMeasurement =
    firstConsolidationMeasurements.find(
      (measurement) => measurement.measurement.primary
    ) ?? firstConsolidationMeasurements[0];
  const consolidationLatestColumn = [
    ...(headlineMeasurement?.columns ?? []),
  ].sort((a, b) => b.column.order - a.column.order)[0];
  const consolidationStatus =
    consolidationLatestColumn?.targetStatusDisplayName.trim() || "";
  const consolidationTargetStatus =
    consolidationLatestColumn?.targetStatus || "";
  const consolidationTrendStatus =
    headlineMeasurement?.trendStatus ||
    (consolidationTargetStatus.includes("BELOW")
      ? "DECLINING"
      : consolidationTargetStatus.includes("ABOVE")
      ? "IMPROVING"
      : "");
  const BannerStatusIcon = consolidationTrendStatus.includes("DECLINING")
    ? TrendingDownIcon
    : consolidationTrendStatus.includes("IMPROVING")
    ? TrendingUpIcon
    : CircleIcon;
  const bannerStatusLabel =
    headlineMeasurement?.trendStatusDisplayName?.trim() ||
    (consolidationTrendStatus.includes("DECLINING")
      ? "Declining"
      : consolidationTrendStatus.includes("IMPROVING")
      ? "Improving"
      : "Status unavailable");
  const consolidationBannerColors = getKpiBannerColors(
    consolidationTargetStatus,
    consolidationTrendStatus
  );
  const targetStatusSentence = consolidationStatus.toLocaleLowerCase();
  const trendStatusSentence =
    headlineMeasurement?.trendStatusDisplayName?.trim().toLocaleLowerCase() ||
    (consolidationTrendStatus ? bannerStatusLabel.toLocaleLowerCase() : "");
  const usesContrastingConjunction =
    (consolidationTargetStatus.includes("ABOVE") &&
      consolidationTrendStatus.includes("DECLINING")) ||
    (consolidationTargetStatus.includes("BELOW") &&
      consolidationTrendStatus.includes("IMPROVING"));
  const combinedStatusSentence = [
    targetStatusSentence,
    trendStatusSentence
      ? `${usesContrastingConjunction ? "but" : "and"} ${trendStatusSentence}`
      : "",
  ]
    .filter(Boolean)
    .join(" ");
  const consolidationHeadline = combinedStatusSentence
    ? `${selectedKpiName} is ${combinedStatusSentence}`
    : selectedKpiName;
  const tabSlotCount = Math.max(isLoading ? 4 : kpiTabs.length, 4);

  if (!isLoading && !isError && kpiTabs.length === 0) {
    return null;
  }

  return (
    <Box
      id="kpi-display"
      component="section"
      aria-labelledby={
        consolidationMemberName ? "fleet-kpi-heading" : undefined
      }
      aria-label={consolidationMemberName ? undefined : "KPI display"}
      sx={{
        mt: 2.5,
        width: "100%",
        fontFamily: '"Nunito", sans-serif',
        "& .MuiTypography-root, & .MuiButtonBase-root": {
          fontFamily: "inherit",
        },
      }}
    >
      <Box sx={{ overflowX: "auto", pb: 0.2 }}>
        <Box
          role="tablist"
          aria-label="Fleet performance metric"
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${tabSlotCount}, minmax(130px, 1fr)) minmax(340px, 3fr)`,
            alignItems: "center",
            minWidth: tabSlotCount * 130 + 340,
            minHeight: 52,
            p: 0.8,
            border: "1px solid #DEE3EC",
            borderRadius: "7px",
            backgroundColor: "#F7F8FC",
          }}
        >
          {isLoading
            ? Array.from({ length: 4 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  height={38}
                  sx={{ mx: 0.2, borderRadius: "5px" }}
                />
              ))
            : kpiTabs.map((kpi) => {
                const isSelected = kpi.id === selectedKpi?.id;

                return (
                  <ButtonBase
                    key={kpi.id}
                    role="tab"
                    type="button"
                    aria-selected={isSelected}
                    onClick={() => {
                      setLocalSelectedKpiId(kpi.id);
                      onKpiSelect?.(kpi.id);
                    }}
                    sx={{
                      ...focusRing,
                      minHeight: 38,
                      mx: 0.2,
                      borderRadius: "5px",
                      color: isSelected ? "#FFFFFF" : "#4F5D73",
                      backgroundColor: isSelected ? "#28A88A" : "transparent",
                      fontSize: 15,
                      fontWeight: 800,
                      transition:
                        "background-color 150ms ease, color 150ms ease",
                      "&:hover": {
                        backgroundColor: isSelected ? "#218D74" : "#ECEFF5",
                      },
                    }}
                  >
                    {kpi.name}
                  </ButtonBase>
                );
              })}

          {!isLoading && isError && (
            <Typography
              sx={{
                gridColumn: `1 / span ${tabSlotCount}`,
                px: 1.5,
                color: isError ? "#B42318" : "#667085",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              Unable to load KPI tabs
            </Typography>
          )}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={1.2}
            sx={{
              gridColumn: tabSlotCount + 1,
              pr: 1.4,
            }}
          >
            <Typography
              component="label"
              htmlFor="kpi-time-period-select"
              sx={{ color: "#667085", fontSize: 12, fontWeight: 700 }}
            >
              Time period
            </Typography>
            <Select
              id="kpi-time-period-select"
              size="small"
              value={displayedTimePeriod}
              disabled={isDisplayLoading || !timePeriodOptions.length}
              inputProps={{ "aria-label": "Time period" }}
              onChange={(event) => {
                if (!selectedKpi) return;
                setSelectedTimePeriods((current) => ({
                  ...current,
                  [selectedKpi.id]: String(event.target.value),
                }));
              }}
              sx={{
                minWidth: 132,
                height: 36,
                color: "#344054",
                backgroundColor: "#FFFFFF",
                fontSize: 12,
                fontWeight: 700,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D0D5DD",
                },
              }}
            >
              {displayedTimePeriod && !hasDisplayedTimePeriodOption && (
                <MenuItem value={displayedTimePeriod}>
                  {displayedTimePeriod}
                </MenuItem>
              )}
              {timePeriodOptions.map((option) => (
                <MenuItem key={String(option.value)} value={String(option.value)}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <Typography
              sx={{ color: "#7B8495", fontSize: 12, fontWeight: 600 }}
            >
              Updated 8 minutes ago
            </Typography>
          </Stack>
        </Box>
      </Box>

      {selectedKpi && (
        <>
          {consolidationMemberName && (
            <Stack
              data-testid="kpi-status-banner"
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={1.5}
              sx={{
                mt: 2.4,
                minHeight: 72,
                px: 2.2,
                py: 1.5,
                border: `1px solid ${consolidationBannerColors.borderColor}`,
                borderRadius: "7px",
                backgroundColor:
                  consolidationBannerColors.bannerBackgroundColor,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: "#5C697C",
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  {consolidationMemberName}
                </Typography>
                <Typography
                  id="fleet-kpi-heading"
                  component="h2"
                  sx={{
                    mt: 0.35,
                    color: consolidationBannerColors.color,
                    fontSize: 17,
                    fontWeight: 800,
                    lineHeight: 1.1,
                  }}
                >
                  {consolidationHeadline}
                </Typography>
              </Box>

              <Stack
                data-testid="kpi-banner-status"
                direction="row"
                alignItems="center"
                justifyContent="center"
                sx={{
                  ml: { xs: 0, sm: "auto" },
                  minWidth: 164,
                  minHeight: 38,
                  px: 1.8,
                  borderRadius: 99,
                  color: consolidationBannerColors.color,
                  backgroundColor: consolidationBannerColors.backgroundColor,
                }}
              >
                <BannerStatusIcon
                  aria-hidden="true"
                  sx={{ mr: 1.1, fontSize: 31 }}
                />
                <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                  {bannerStatusLabel}
                </Typography>
              </Stack>
            </Stack>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2.4,
              mt: 2.4,
            }}
          >
            {isDisplayLoading ? (
              Array.from({ length: 2 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  height={242}
                  sx={{ borderRadius: "7px" }}
                />
              ))
            ) : isDisplayError ? (
              <Box
                role="alert"
                sx={{
                  gridColumn: "1 / -1",
                  py: 4,
                  border: "1px solid #FECDCA",
                  borderRadius: "7px",
                  color: "#B42318",
                  backgroundColor: "#FFFBFA",
                  fontSize: 12,
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                Unable to load KPI items
              </Box>
            ) : (
              <>
                <VehiclePanel
                  title="Worst performing"
                  vehicles={attentionItems}
                  tone="attention"
                  onVehicleDetails={onVehicleDetails}
                />
                <VehiclePanel
                  title="Best performing"
                  vehicles={performingItems}
                  tone="performing"
                  onVehicleDetails={onVehicleDetails}
                />
              </>
            )}
          </Box>

          {consolidationGroups.map((group) => (
            <Box key={group.id}>
              <Typography
                sx={{
                  mt: 3,
                  mb: 1.2,
                  color: "#344054",
                  fontSize: 15,
                  fontWeight: 800,
                }}
              >
                {group.title}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: `repeat(${Math.min(
                      group.items.length,
                      4
                    )}, minmax(0, 1fr))`,
                  },
                  gap: 1.8,
                }}
              >
                {group.items.map((summary) => {
                  const targetColors = getTargetStatusColors(
                    summary.targetStatus
                  );

                  return (
                    <Stack
                      key={summary.id}
                      direction="row"
                      alignItems="center"
                      sx={{
                        minHeight: 60,
                        px: 1.8,
                        py: 1.1,
                        border: "1px solid #E0E5ED",
                        borderRadius: "7px",
                        backgroundColor: "#F8F9FB",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            color: "#6D7788",
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: 1.1,
                          }}
                        >
                          {summary.period}
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.35,
                            color: "#344054",
                            fontSize: 15,
                            fontWeight: 800,
                            lineHeight: 1,
                          }}
                        >
                          {summary.value}
                        </Typography>
                      </Box>
                      <Stack ml="auto" alignItems="flex-end">
                        <Box
                          sx={{
                            minWidth: 91,
                            px: 1.4,
                            py: 0.7,
                            borderRadius: 99,
                            color: targetColors.color,
                            backgroundColor: targetColors.backgroundColor,
                            fontSize: 12,
                            fontWeight: 800,
                            lineHeight: 1,
                            textAlign: "center",
                          }}
                        >
                          {summary.badge}
                        </Box>
                        <TrendIndicator
                          trendStatus={summary.trendStatus}
                          trendLabel={summary.trendLabel}
                          ariaLabel={summary.trendAriaLabel}
                        />
                      </Stack>
                    </Stack>
                  );
                })}
              </Box>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}
